import React, { useState, useEffect, useCallback } from "react";
import { api } from "./api";
import GestioneUtenti from "./GestioneUtenti";
import { Icon } from "./Icons";

// ── Design system (allineato alle altre tab) ──────────────────────────────────
const C = {
  dark:      "#1A1A1A",
  blue:      "#1565C0",
  grey:      "#546E7A",
  light:     "#F5F5F5",
  border:    "#E0E0E0",
  text:      "#212121",
  textSub:   "#757575",
  error:     "#B71C1C",
  errorLight:"#FFEBEE",
  success:   "#2E7D32",
  successLight: "#E8F5E9",
  bluLight:  "#E3F2FD",
};
const FONT      = "Roboto, 'Segoe UI', Arial, sans-serif";
const FONT_SIZE = 15;
const ROW_H     = 44;

// ── Stili condivisi ───────────────────────────────────────────────────────────
const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
};
const modaleStyle = {
  background: "#fff", borderRadius: 10, padding: 28, minWidth: 360,
  boxShadow: "0 8px 30px rgba(0,0,0,0.22)", display: "flex", flexDirection: "column",
  gap: 14, fontFamily: FONT,
};
const labelStyle = {
  fontSize: 12, fontWeight: 700, color: C.textSub,
  textTransform: "uppercase", letterSpacing: 0.6,
  display: "block", marginBottom: 5,
};
const inputStyle = {
  border: `1.5px solid ${C.border}`, borderRadius: 6, padding: "9px 12px",
  fontSize: FONT_SIZE, width: "100%", boxSizing: "border-box",
  outline: "none", fontFamily: FONT, color: C.text,
};
const btnPrimary = {
  background: C.blue, color: "#fff", border: "none", borderRadius: 6,
  padding: "9px 20px", cursor: "pointer", fontWeight: 700,
  fontSize: FONT_SIZE, fontFamily: FONT,
};
const btnSecondary = {
  background: C.light, color: C.text, border: `1px solid ${C.border}`,
  borderRadius: 6, padding: "9px 20px", cursor: "pointer",
  fontWeight: 600, fontSize: FONT_SIZE, fontFamily: FONT,
};
const thStyle = {
  padding: "10px 14px", fontWeight: 700, textAlign: "left",
  borderBottom: `2px solid ${C.border}`, color: C.grey,
  fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5,
  background: C.light,
};
const tdStyle = {
  padding: "0 14px", height: ROW_H, borderBottom: `1px solid ${C.border}`,
  verticalAlign: "middle", fontSize: FONT_SIZE, color: C.text,
};
const iconBtn = {
  background: "none", border: `1px solid ${C.border}`, borderRadius: 5,
  width: 32, height: 32, cursor: "pointer", fontSize: 14, color: C.grey,
  display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0,
};

// ── Badge stato ───────────────────────────────────────────────────────────────
function BadgeStato({ attivo }) {
  return (
    <span style={{
      display: "inline-block", borderRadius: 12, padding: "3px 12px",
      fontSize: 12, fontWeight: 700,
      background: attivo ? C.successLight : C.light,
      color: attivo ? C.success : C.textSub,
    }}>
      {attivo ? "Attivo" : "Disattivo"}
    </span>
  );
}

// ── Modale Operatore ──────────────────────────────────────────────────────────
function ModaleOperatore({ operatore, onClose, onSave }) {
  const [nome,     setNome]     = useState(operatore?.nome || "");
  const [cognome,  setCognome]  = useState(operatore?.cognome || "");
  const [capacita, setCapacita] = useState(operatore?.capacita_settimanale ?? 180);
  const [loading,  setLoading]  = useState(false);
  const [err,      setErr]      = useState("");

  const submit = async () => {
    if (!nome.trim() || !cognome.trim()) { setErr("Nome e Cognome obbligatori"); return; }
    const cap = parseFloat(capacita) || 180;
    setLoading(true);
    try {
      await onSave({ nome: nome.trim(), cognome: cognome.trim(), capacita_settimanale: cap });
      onClose();
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };

  return (
    <div style={overlayStyle}>
      <div style={modaleStyle}>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.dark }}>
          {operatore ? "Modifica Operatore" : "Nuovo Operatore"}
        </div>
        <div>
          <label style={labelStyle}>Cognome *</label>
          <input style={inputStyle} value={cognome} onChange={e => setCognome(e.target.value)} placeholder="es. Rossi" autoFocus />
        </div>
        <div>
          <label style={labelStyle}>Nome *</label>
          <input style={inputStyle} value={nome} onChange={e => setNome(e.target.value)} placeholder="es. Mario" />
        </div>
        <div>
          <label style={labelStyle}>Capacità settimanale (colli)</label>
          <input style={inputStyle} type="number" min={0} value={capacita}
            onChange={e => setCapacita(e.target.value)} placeholder="es. 180" />
        </div>
        {err && (
          <div style={{ background: C.errorLight, color: C.error, borderRadius: 6, padding: "8px 12px", fontSize: 13, fontWeight: 600 }}>
            {err}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <button onClick={onClose} style={btnSecondary}>Annulla</button>
          <button onClick={submit} disabled={loading} style={btnPrimary}>{loading ? "..." : "Salva"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Modale Reparto ────────────────────────────────────────────────────────────
function ModaleReparto({ reparto, onClose, onSave }) {
  const [nome,          setNome]          = useState(reparto?.nome || "");
  const [scaricaColli,  setScaricaColli]  = useState(reparto?.scarica_colli ?? 1);
  const [colliPersona,  setColliPersona]  = useState(reparto?.colli_per_persona ?? 0);
  const [loading,       setLoading]       = useState(false);
  const [err,           setErr]           = useState("");

  const submit = async () => {
    if (!nome.trim()) { setErr("Nome obbligatorio"); return; }
    setLoading(true);
    try {
      await onSave({ nome: nome.trim(), scarica_colli: scaricaColli, colli_per_persona: parseFloat(colliPersona) || 0 });
      onClose();
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };

  return (
    <div style={overlayStyle}>
      <div style={modaleStyle}>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.dark }}>
          {reparto ? "Modifica Reparto" : "Nuovo Reparto"}
        </div>
        <div>
          <label style={labelStyle}>Nome reparto *</label>
          <input style={inputStyle} value={nome} onChange={e => setNome(e.target.value)} placeholder="es. Assemblaggio" autoFocus />
        </div>
        <div>
          <label style={{ ...labelStyle, marginBottom: 10 }}>Comportamento colli</label>
          <label style={{
            display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
            fontSize: FONT_SIZE, color: C.text,
          }}>
            <input
              type="checkbox"
              checked={scaricaColli === 1}
              onChange={e => setScaricaColli(e.target.checked ? 1 : 0)}
              style={{ width: 16, height: 16, cursor: "pointer" }}
            />
            Scarica colli (conta nei calcoli di versato/anticipato)
          </label>
          {scaricaColli === 0 && (
            <div style={{
              marginTop: 8, fontSize: 12, color: C.textSub,
              background: C.light, borderRadius: 5, padding: "6px 10px",
              border: `1px solid ${C.border}`,
            }}>
              ℹ Reparto "in ombra": la produzione di questo reparto non verrà conteggiata come versato o anticipo.
            </div>
          )}
        </div>
        <div>
          <label style={labelStyle}>Colli / settimana per persona</label>
          <input
            style={inputStyle} type="number" min={0} value={colliPersona}
            onChange={e => setColliPersona(e.target.value)}
            placeholder="es. 200"
          />
          <div style={{ marginTop: 6, fontSize: 12, color: C.textSub }}>
            Usato per calcolare il head count necessario nella tab Dettaglio / Consuntivo.
          </div>
        </div>
        {err && (
          <div style={{ background: C.errorLight, color: C.error, borderRadius: 6, padding: "8px 12px", fontSize: 13, fontWeight: 600 }}>
            {err}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <button onClick={onClose} style={btnSecondary}>Annulla</button>
          <button onClick={submit} disabled={loading} style={btnPrimary}>{loading ? "..." : "Salva"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Modale modifica riga pianificazione ───────────────────────────────────────
function ModaleModificaPiano({ riga, commesse, weeks, onClose, onSave }) {
  const [container,    setContainer]    = useState(riga.container_schedulato || "");
  const [colliNom,     setColliNom]     = useState(riga.colli_nominali ?? 0);
  const [loading,      setLoading]      = useState(false);
  const [err,          setErr]          = useState("");

  const commessa = commesse.find(c => c.id === riga.commessa_id);
  const week     = weeks.find(w => w.id === riga.week_id);

  const submit = async () => {
    setLoading(true);
    try {
      await onSave(riga.id, {
        commessa_id:         riga.commessa_id,
        week_id:             riga.week_id,
        container_schedulato: container.trim() || null,
        colli_nominali:      parseFloat(colliNom) || 0,
      });
      onClose();
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };

  return (
    <div style={overlayStyle}>
      <div style={modaleStyle}>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.dark }}>
          Modifica pianificazione
        </div>
        <div style={{ fontSize: 13, color: C.textSub }}>
          <strong>{commessa?.codice ?? riga.commessa_id}</strong> — {week?.nome ?? riga.week_id}
        </div>
        <div>
          <label style={labelStyle}>Container schedulato</label>
          <input style={inputStyle} value={container} onChange={e => setContainer(e.target.value)}
            placeholder="es. CNT 1" autoFocus />
        </div>
        <div>
          <label style={labelStyle}>Colli nominali</label>
          <input style={inputStyle} type="number" min={0} value={colliNom}
            onChange={e => setColliNom(e.target.value)} />
        </div>
        {err && (
          <div style={{ background: C.errorLight, color: C.error, borderRadius: 6, padding: "8px 12px", fontSize: 13, fontWeight: 600 }}>
            {err}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <button onClick={onClose} style={btnSecondary}>Annulla</button>
          <button onClick={submit} disabled={loading} style={btnPrimary}>{loading ? "..." : "Salva"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Sezione CRUD generica ─────────────────────────────────────────────────────
function SezioneCrud({ titolo, contatore, onAggiungi, loading, vuotoMsg, children }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 8,
      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      border: `1px solid ${C.border}`,
      overflow: "hidden", marginBottom: 24,
    }}>
      {/* Header sezione */}
      <div style={{
        background: C.grey, color: "#fff", padding: "12px 20px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, fontFamily: FONT }}>{titolo}</div>
          <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2, fontFamily: FONT }}>{contatore}</div>
        </div>
        <button onClick={onAggiungi} style={{
          background: "rgba(255,255,255,0.18)", border: "1.5px solid rgba(255,255,255,0.5)",
          color: "#fff", borderRadius: 6, padding: "8px 18px",
          cursor: "pointer", fontWeight: 700, fontSize: FONT_SIZE, fontFamily: FONT,
        }}>
          + Aggiungi
        </button>
      </div>
      {loading && (
        <div style={{ padding: 32, textAlign: "center", color: C.textSub, fontSize: FONT_SIZE, fontFamily: FONT }}>
          Caricamento...
        </div>
      )}
      {!loading && !children && (
        <div style={{ padding: 32, textAlign: "center", color: C.textSub, fontSize: FONT_SIZE, fontFamily: FONT }}>
          {vuotoMsg}
        </div>
      )}
      {!loading && children}
    </div>
  );
}

// ── Drawer: scheda a tutto schermo che entra da destra ────────────────────────
function Drawer({ open, title, onClose, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", justifyContent: "flex-end" }}>
      <style>{"@keyframes drawerIn{from{transform:translateX(100%)}to{transform:translateX(0)}}"}</style>
      <div onClick={e => e.stopPropagation()} style={{
        width: "min(960px, 100%)", height: "100%", background: C.light,
        boxShadow: "-8px 0 30px rgba(0,0,0,0.25)", display: "flex", flexDirection: "column",
        animation: "drawerIn .2s ease-out",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", background: C.dark, color: "#fff", flexShrink: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 17, fontFamily: FONT }}>{title}</div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.4)", color: "#fff", borderRadius: 6, padding: "7px 14px", cursor: "pointer", fontWeight: 700, fontSize: 14, fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Icon name="close" size={16} color="#fff" /> Chiudi
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Elenco cliccabile delle sezioni ───────────────────────────────────────────
function ElencoSezioni({ items, onOpen }) {
  return (
    <div style={{ background: "#fff", borderRadius: 8, border: `1px solid ${C.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" }}>
      {items.map((s, i) => (
        <button key={s.key} onClick={() => onOpen(s.key)}
          style={{ display: "flex", alignItems: "center", gap: 16, width: "100%", textAlign: "left", background: "#fff", border: "none", borderTop: i ? `1px solid ${C.border}` : "none", padding: "16px 20px", cursor: "pointer", fontFamily: FONT }}
          onMouseEnter={e => e.currentTarget.style.background = C.bluLight}
          onMouseLeave={e => e.currentTarget.style.background = "#fff"}
        >
          <Icon name={s.icon} size={26} color={C.grey} style={{ width: 30 }} />
          <span style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{s.label}</div>
            <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>{s.sub}</div>
          </span>
          <Icon name="chevron_right" size={22} color={C.textSub} />
        </button>
      ))}
    </div>
  );
}

// ── Componente principale ─────────────────────────────────────────────────────
export default function Impostazioni() {
  const [operatori,   setOperatori]   = useState([]);
  const [reparti,     setReparti]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [modaleOp,    setModaleOp]    = useState(null);
  const [modaleRep,   setModaleRep]   = useState(null);
  const [apertaKey,   setApertaKey]   = useState(null);   // sezione aperta nel drawer

  // Archivio (commesse / settimane archiviate)
  const [commesseArch, setCommesseArch] = useState([]);
  const [weeksArch,    setWeeksArch]    = useState([]);

  // Sezione pianificazione
  const [piani,       setPiani]       = useState([]);
  const [weeks,       setWeeks]       = useState([]);
  const [commesse,    setCommesse]    = useState([]);
  const [pianoLoading,setPianoLoading]= useState(false);
  const [filtroWeek,  setFiltroWeek]  = useState("tutti");
  const [modalePiano, setModalePiano] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [ops, reps, comm, ws] = await Promise.all([
        api.getOperatori(true),
        api.getReparti(true),
        api.getCommesse(true),
        api.getWeeks(true),
      ]);
      setOperatori(ops);
      setReparti(reps);
      setCommesseArch(comm.filter(c => c.archiviata === 1));
      setWeeksArch(ws.filter(w => w.archiviata === 1));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleRipristinaCommessa = async (c) => {
    try { await api.patchCommessa(c.id, { archiviata: 0 }); await load(); }
    catch (e) { setError(e.message); }
  };

  const handleRipristinaWeek = async (w) => {
    try { await api.updateWeek(w.id, { archiviata: 0 }); await load(); }
    catch (e) { setError(e.message); }
  };

  const loadPiani = useCallback(async () => {
    setPianoLoading(true);
    try {
      const [lista, ws, cs] = await Promise.all([
        api.getPianificazioneLista(),
        api.getWeeks(),
        api.getCommesse(),
      ]);
      setPiani(lista);
      setWeeks(ws);
      setCommesse(cs);
    } catch (e) { setError(e.message); }
    finally { setPianoLoading(false); }
  }, []);

  useEffect(() => { load(); loadPiani(); }, [loadPiani]);

  const handleSaveOp = async (data) => {
    if (modaleOp && modaleOp !== "nuovo") {
      await api.updateOperatore(modaleOp.id, data);
    } else {
      await api.createOperatore(data);
    }
    await load();
  };

  const handleToggleAttivo = async (op) => {
    await api.updateOperatore(op.id, { attivo: op.attivo === 1 ? 0 : 1 });
    await load();
  };

  const handleDeleteOp = async (op) => {
    if (!window.confirm(`Eliminare ${op.cognome} ${op.nome}? Verranno eliminati anche tutti i dati di dettaglio associati.`)) return;
    try { await api.deleteOperatore(op.id); await load(); }
    catch (e) { setError(e.message); }
  };

  const handleSaveRep = async (data) => {
    if (modaleRep && modaleRep !== "nuovo") {
      await api.updateReparto(modaleRep.id, data);
    } else {
      await api.createReparto(data);
    }
    await load();
  };

  const handleToggleReparto = async (rep) => {
    await api.updateReparto(rep.id, { attivo: rep.attivo === 1 ? 0 : 1 });
    await load();
  };

  const handleDeleteRep = async (rep) => {
    if (!window.confirm(`Eliminare il reparto "${rep.nome}"?`)) return;
    try { await api.deleteReparto(rep.id); await load(); }
    catch (e) { setError(e.message); }
  };

  const handleDeletePiano = async (p) => {
    const commessa = commesse.find(c => c.id === p.commessa_id);
    const week     = weeks.find(w => w.id === p.week_id);
    if (!window.confirm(`Eliminare la riga "${commessa?.codice ?? p.commessa_id}" — ${week?.nome ?? p.week_id}?`)) return;
    try { await api.deletePianificazione(p.id); await loadPiani(); }
    catch (e) { setError(e.message); }
  };

  const handleSavePiano = async (id, data) => {
    await api.patchPianificazione(id, data);
    await loadPiani();
  };

  const pianoFiltrati = filtroWeek === "tutti"
    ? piani
    : piani.filter(p => String(p.week_id) === String(filtroWeek));

  const attiviOp     = operatori.filter(o => o.attivo === 1);
  const disattiviOp  = operatori.filter(o => o.attivo !== 1);
  const attiviRep    = reparti.filter(r => r.attivo === 1);
  const disattiviRep = reparti.filter(r => r.attivo !== 1);

  const SEZIONI = [
    { key: "utenti",         icon: "person",   label: "Gestione utenti", sub: "Account e ruoli di accesso" },
    { key: "log",            icon: "history",  label: "Log attività",    sub: "Storico delle scritture" },
    { key: "operatori",      icon: "groups",   label: "Operatori",       sub: `${attiviOp.length} attivi${disattiviOp.length > 0 ? `, ${disattiviOp.length} disattivati` : ""}` },
    { key: "reparti",        icon: "domain",   label: "Reparti",         sub: `${attiviRep.length} attivi${disattiviRep.length > 0 ? `, ${disattiviRep.length} disattivati` : ""}` },
    { key: "pianificazione", icon: "calendar", label: "Pianificazione",  sub: `${piani.length} righe` },
    { key: "archivio",       icon: "archive",  label: "Archivio",        sub: `${commesseArch.length} commesse, ${weeksArch.length} settimane` },
  ];
  const titoloAperta = SEZIONI.find(s => s.key === apertaKey)?.label || "";

  return (
    <div style={{ padding: 24, fontFamily: FONT, background: C.light, minHeight: "100vh" }}>
      <div style={{ maxWidth: 760 }}>

        {error && (
          <div style={{
            background: C.errorLight, border: `1px solid #EF9A9A`,
            color: C.error, padding: "10px 14px", borderRadius: 6,
            marginBottom: 16, fontSize: FONT_SIZE, fontWeight: 600,
            display: "flex", alignItems: "center",
          }}>
            <Icon name="warning" size={16} color={C.error} style={{ marginRight: 6 }} />{error}
            <button onClick={() => setError("")} style={{
              marginLeft: "auto", cursor: "pointer", background: "none",
              border: "none", fontWeight: 700, fontSize: 18, color: C.error,
            }}>×</button>
          </div>
        )}

        {/* ── Elenco cliccabile delle sezioni ── */}
        <ElencoSezioni items={SEZIONI} onOpen={setApertaKey} />
      </div>

      {/* ── Dettaglio sezione: scheda a tutto schermo da destra ── */}
      <Drawer open={!!apertaKey} title={titoloAperta} onClose={() => setApertaKey(null)}>
        {apertaKey === "utenti" && <GestioneUtenti view="utenti" />}
        {apertaKey === "log" && <GestioneUtenti view="log" />}

        {apertaKey === "operatori" && (
        <SezioneCrud
          titolo="Operatori"
          contatore={`${attiviOp.length} attivi${disattiviOp.length > 0 ? `, ${disattiviOp.length} disattivati` : ""}`}
          onAggiungi={() => setModaleOp("nuovo")}
          loading={loading}
          vuotoMsg='Nessun operatore. Clicca "+ Aggiungi" per iniziare.'
        >
          {operatori.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: FONT_SIZE, fontFamily: FONT }}>
              <thead>
                <tr>
                  <th style={thStyle}>Cognome</th>
                  <th style={thStyle}>Nome</th>
                  <th style={{ ...thStyle, width: 140, textAlign: "center" }}>Cap. sett. (colli)</th>
                  <th style={{ ...thStyle, width: 100, textAlign: "center" }}>Stato</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "center" }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {operatori.map(op => (
                  <tr key={op.id}
                    style={{ opacity: op.attivo === 1 ? 1 : 0.5 }}
                    onMouseEnter={e => e.currentTarget.style.background = C.bluLight}
                    onMouseLeave={e => e.currentTarget.style.background = ""}
                  >
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{op.cognome}</td>
                    <td style={tdStyle}>{op.nome}</td>
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: 600 }}>
                      {(op.capacita_settimanale ?? 180).toLocaleString("it-IT")}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <BadgeStato attivo={op.attivo === 1} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                        <button onClick={() => setModaleOp(op)} title="Modifica" style={iconBtn}><Icon name="edit" size={16} color={C.grey} /></button>
                        <button onClick={() => handleToggleAttivo(op)} title={op.attivo === 1 ? "Disattiva" : "Riattiva"} style={iconBtn}>
                          {op.attivo === 1 ? <Icon name="pause" size={15} color={C.grey} /> : <Icon name="play" size={15} color={C.grey} />}
                        </button>
                        <button onClick={() => handleDeleteOp(op)} title="Elimina" style={{ ...iconBtn, color: C.error }}>×</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SezioneCrud>
        )}

        {apertaKey === "reparti" && (
        <SezioneCrud
          titolo="Reparti"
          contatore={`${attiviRep.length} attivi${disattiviRep.length > 0 ? `, ${disattiviRep.length} disattivati` : ""}`}
          onAggiungi={() => setModaleRep("nuovo")}
          loading={loading}
          vuotoMsg='Nessun reparto. Clicca "+ Aggiungi" per iniziare.'
        >
          {reparti.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: FONT_SIZE, fontFamily: FONT }}>
              <thead>
                <tr>
                  <th style={thStyle}>Nome reparto</th>
                  <th style={{ ...thStyle, width: 130, textAlign: "center" }}>Scarica colli</th>
                  <th style={{ ...thStyle, width: 130, textAlign: "center" }}>Colli/pers./sett.</th>
                  <th style={{ ...thStyle, width: 100, textAlign: "center" }}>Stato</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "center" }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {reparti.map(rep => (
                  <tr key={rep.id}
                    style={{ opacity: rep.attivo === 1 ? 1 : 0.5 }}
                    onMouseEnter={e => e.currentTarget.style.background = C.bluLight}
                    onMouseLeave={e => e.currentTarget.style.background = ""}
                  >
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{rep.nome}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <span style={{
                        display: "inline-block", borderRadius: 12, padding: "3px 12px",
                        fontSize: 12, fontWeight: 700,
                        background: rep.scarica_colli === 1 ? C.successLight : "#FFF8E1",
                        color: rep.scarica_colli === 1 ? C.success : "#F57F17",
                      }}>
                        {rep.scarica_colli === 1 ? "Sì" : "In ombra"}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: 600 }}>
                      {(rep.colli_per_persona ?? 0) > 0 ? (rep.colli_per_persona).toLocaleString("it-IT") : "—"}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <BadgeStato attivo={rep.attivo === 1} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                        <button onClick={() => setModaleRep(rep)} title="Modifica" style={iconBtn}><Icon name="edit" size={16} color={C.grey} /></button>
                        <button onClick={() => handleToggleReparto(rep)} title={rep.attivo === 1 ? "Disattiva" : "Riattiva"} style={iconBtn}>
                          {rep.attivo === 1 ? <Icon name="pause" size={15} color={C.grey} /> : <Icon name="play" size={15} color={C.grey} />}
                        </button>
                        <button onClick={() => handleDeleteRep(rep)} title="Elimina" style={{ ...iconBtn, color: C.error }}>×</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SezioneCrud>
        )}

        {apertaKey === "pianificazione" && (
        <div style={{
          background: "#fff", borderRadius: 8,
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          border: `1px solid ${C.border}`,
          overflow: "hidden", marginBottom: 24,
        }}>
          {/* Header */}
          <div style={{
            background: C.grey, color: "#fff", padding: "12px 20px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, fontFamily: FONT }}>Pianificazione</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2, fontFamily: FONT }}>
                {pianoFiltrati.length} righe
              </div>
            </div>
            {/* Filtro week */}
            <select
              value={filtroWeek}
              onChange={e => setFiltroWeek(e.target.value)}
              style={{
                border: "1.5px solid rgba(255,255,255,0.5)", borderRadius: 6,
                padding: "7px 12px", fontSize: FONT_SIZE, fontFamily: FONT,
                background: "rgba(255,255,255,0.15)", color: "#fff",
                cursor: "pointer", outline: "none",
              }}
            >
              <option value="tutti" style={{ color: C.text, background: "#fff" }}>Tutte le settimane</option>
              {weeks.map(w => (
                <option key={w.id} value={w.id} style={{ color: C.text, background: "#fff" }}>{w.nome}</option>
              ))}
            </select>
          </div>

          {pianoLoading && (
            <div style={{ padding: 32, textAlign: "center", color: C.textSub, fontSize: FONT_SIZE, fontFamily: FONT }}>
              Caricamento...
            </div>
          )}

          {!pianoLoading && pianoFiltrati.length === 0 && (
            <div style={{ padding: 32, textAlign: "center", color: C.textSub, fontSize: FONT_SIZE, fontFamily: FONT }}>
              Nessuna riga trovata.
            </div>
          )}

          {!pianoLoading && pianoFiltrati.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: FONT_SIZE, fontFamily: FONT }}>
              <thead>
                <tr>
                  <th style={thStyle}>Settimana</th>
                  <th style={thStyle}>Commessa</th>
                  <th style={thStyle}>Container</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Nom.</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Pian.</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Vers.</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Ant.</th>
                  <th style={{ ...thStyle, width: 90, textAlign: "center" }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {pianoFiltrati.map(p => {
                  const commessa = commesse.find(c => c.id === p.commessa_id);
                  const week     = weeks.find(w => w.id === p.week_id);
                  return (
                    <tr key={p.id}
                      onMouseEnter={e => e.currentTarget.style.background = C.bluLight}
                      onMouseLeave={e => e.currentTarget.style.background = ""}
                    >
                      <td style={{ ...tdStyle, fontWeight: 600, color: C.blue }}>{week?.nome ?? p.week_id}</td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{commessa?.codice ?? p.commessa_id}</td>
                      <td style={{ ...tdStyle, color: p.container_schedulato ? C.text : C.textSub, fontStyle: p.container_schedulato ? "normal" : "italic" }}>
                        {p.container_schedulato || "—"}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>{(p.colli_nominali ?? 0).toLocaleString("it-IT")}</td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>{(p.colli_pianificati ?? 0).toLocaleString("it-IT")}</td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>{(p.colli_versati ?? 0).toLocaleString("it-IT")}</td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>{(p.colli_anticipati ?? 0).toLocaleString("it-IT")}</td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                          <button onClick={() => setModalePiano(p)} title="Modifica" style={iconBtn}><Icon name="edit" size={16} color={C.grey} /></button>
                          <button onClick={() => handleDeletePiano(p)} title="Elimina" style={{ ...iconBtn, color: C.error }}>×</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        )}

        {apertaKey === "archivio" && (
        <div style={{
          background: "#fff", borderRadius: 8,
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          border: `1px solid ${C.border}`,
          overflow: "hidden", marginBottom: 24,
        }}>
          <div style={{ background: C.grey, color: "#fff", padding: "12px 20px" }}>
            <div style={{ fontWeight: 700, fontSize: 16, fontFamily: FONT }}>Archivio</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2, fontFamily: FONT }}>
              {commesseArch.length} commesse, {weeksArch.length} settimane archiviate
            </div>
          </div>

          {commesseArch.length === 0 && weeksArch.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: C.textSub, fontSize: FONT_SIZE, fontFamily: FONT }}>
              Nessun elemento archiviato.
            </div>
          ) : (
            <div style={{ padding: "8px 0" }}>
              {commesseArch.length > 0 && (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: FONT_SIZE, fontFamily: FONT }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Commessa archiviata</th>
                      <th style={{ ...thStyle, width: 140, textAlign: "center" }}>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commesseArch.map(c => (
                      <tr key={c.id}
                        onMouseEnter={e => e.currentTarget.style.background = C.bluLight}
                        onMouseLeave={e => e.currentTarget.style.background = ""}
                      >
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{c.codice}{c.descrizione ? ` — ${c.descrizione}` : ""}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <button onClick={() => handleRipristinaCommessa(c)} style={{ ...btnSecondary, display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="restore" size={16} /> Ripristina</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {weeksArch.length > 0 && (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: FONT_SIZE, fontFamily: FONT, marginTop: commesseArch.length > 0 ? 8 : 0 }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Settimana archiviata</th>
                      <th style={{ ...thStyle, width: 140, textAlign: "center" }}>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeksArch.map(w => (
                      <tr key={w.id}
                        onMouseEnter={e => e.currentTarget.style.background = C.bluLight}
                        onMouseLeave={e => e.currentTarget.style.background = ""}
                      >
                        <td style={{ ...tdStyle, fontWeight: 600, color: C.blue }}>{w.nome}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <button onClick={() => handleRipristinaWeek(w)} style={{ ...btnSecondary, display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="restore" size={16} /> Ripristina</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
        )}
      </Drawer>

      {/* Modali */}
      {modaleOp && (
        <ModaleOperatore
          operatore={modaleOp === "nuovo" ? null : modaleOp}
          onClose={() => setModaleOp(null)}
          onSave={handleSaveOp}
        />
      )}
      {modaleRep && (
        <ModaleReparto
          reparto={modaleRep === "nuovo" ? null : modaleRep}
          onClose={() => setModaleRep(null)}
          onSave={handleSaveRep}
        />
      )}
      {modalePiano && (
        <ModaleModificaPiano
          riga={modalePiano}
          commesse={commesse}
          weeks={weeks}
          onClose={() => setModalePiano(null)}
          onSave={handleSavePiano}
        />
      )}
    </div>
  );
}
